import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import DataTable, { TableColumn } from "react-data-table-component";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Client } from "../types";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

interface ServiceData {
  serviceName: string;
  clientCount: number;
  clientTypes: { [key: string]: number };
  totalRevenue?: number;
}

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#1976d2",
      color: "#fff",
      fontWeight: 500,
    },
  },
  headCells: {
    style: {
      padding: "12px",
    },
  },
  rows: {
    style: {
      "&:nth-of-type(odd)": {
        backgroundColor: "#f5f5f5",
      },
      "&:hover": {
        backgroundColor: "#e0f7fa",
      },
    },
  },
  cells: {
    style: {
      padding: "12px",
    },
  },
};

const ServiceReports = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [clients, setClients] = useState<Client[]>([]);
  const [serviceData, setServiceData] = useState<ServiceData[]>([]);
  const [filteredServices, setFilteredServices] = useState<ServiceData[]>([]);
  const [search, setSearch] = useState("");
  const [clientTypeFilter, setClientTypeFilter] = useState("");
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllServicesByType, setShowAllServicesByType] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setLoading(false); return; }

      const authHeader = `Bearer ${token}`;
      const apiUrl = getApiBaseUrl();

      const response = await fetch(`${apiUrl}/api/clients`, {
        method: "GET",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setClients(data.clients);
      processServiceData(data.clients);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
    } finally {
      setLoading(false);
    }
  };

  const processServiceData = (clientsData: Client[]) => {
    const serviceMap: { [key: string]: ServiceData } = {};

    clientsData.forEach((client) => {
      if (typeof client.Services === "string" && client.Services) {
        const services = client.Services.split(";")
          .map((s) => s.trim())
          .filter((s) => s);
        services.forEach((service) => {
          if (!serviceMap[service]) {
            serviceMap[service] = {
              serviceName: service,
              clientCount: 0,
              clientTypes: {},
            };
          }
          serviceMap[service].clientCount++;

          const clientType = client.ClientType || "Unknown";
          serviceMap[service].clientTypes[clientType] =
            (serviceMap[service].clientTypes[clientType] || 0) + 1;
        });
      }
    });

    const processed = Object.values(serviceMap).sort(
      (a, b) => b.clientCount - a.clientCount,
    );
    setServiceData(processed);
    setFilteredServices(processed);
  };

  useEffect(() => {
    fetchData();
  }, [instance]);

  useEffect(() => {
    let filtered = serviceData;

    if (search) {
      filtered = filtered.filter((service) =>
        service.serviceName.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (clientTypeFilter) {
      filtered = filtered.filter(
        (service) => service.clientTypes[clientTypeFilter] > 0,
      );
    }

    setFilteredServices(filtered);
  }, [search, clientTypeFilter, serviceData]);

  const downloadCSV = () => {
    const headers = [
      "Service Name",
      "Client Count",
      "Client Types (Type: Count)",
    ];
    const rows = filteredServices
      .map((service) => {
        const clientTypesStr = Object.entries(service.clientTypes)
          .map(([type, count]) => `${type}: ${count}`)
          .join("; ");
        return [
          `"${service.serviceName}"`,
          service.clientCount,
          `"${clientTypesStr}"`,
        ].join(",");
      })
      .join("\n");

    const csvContent = `${headers.join(",")}\n${rows}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "service_reports.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Popular Services Chart Data
  const displayedServices = showAllServices
    ? serviceData
    : serviceData.slice(0, 10);
  const popularServicesData = {
    labels: displayedServices.map((service) => service.serviceName),
    datasets: [
      {
        label: "Number of Clients",
        data: displayedServices.map((service) => service.clientCount),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  const popularServicesOptions = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Clients",
          font: {
            size: 12,
            weight: 500,
          },
        },
        grid: {
          color: "#f1f5f9",
        },
      },
      y: {
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Services by Client Type Chart Data
  const clientTypes = Array.from(
    new Set(clients.map((client) => client.ClientType)),
  ).filter(Boolean);
  const servicesByTypeData: { [key: string]: number } = {};

  clientTypes.forEach((type) => {
    servicesByTypeData[type] = clients.filter(
      (client) =>
        client.ClientType === type &&
        typeof client.Services === "string" &&
        client.Services,
    ).length;
  });

  const displayedTypes = showAllServicesByType
    ? Object.entries(servicesByTypeData)
    : Object.entries(servicesByTypeData).slice(0, 8);

  const servicesByTypeChartData = {
    labels: displayedTypes.map(([type]) => type),
    datasets: [
      {
        label: "Clients with Services",
        data: displayedTypes.map(([, count]) => count),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(199, 199, 199, 0.6)",
          "rgba(83, 102, 255, 0.6)",
        ],
        borderColor: "rgba(0, 0, 0, 1)",
        borderWidth: 1,
      },
    ],
  };

  const servicesByTypeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
      },
    },
  };

  // Data Table Columns
  const columns: TableColumn<ServiceData>[] = [
    {
      name: "Service Name",
      selector: (row: ServiceData) => row.serviceName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Client Count",
      selector: (row: ServiceData) => row.clientCount,
      sortable: true,
      center: true,
    },
    {
      name: "Top Client Types",
      cell: (row: ServiceData) => {
        const topTypes = Object.entries(row.clientTypes)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 3)
          .map(([type, count]) => `${type} (${count})`)
          .join(", ");
        return <span>{topTypes}</span>;
      },
      wrap: true,
    },
  ];

  const uniqueClientTypes = Array.from(
    new Set(clients.map((client) => client.ClientType)),
  )
    .filter(Boolean)
    .sort();

  if (error) return <div>Error: {error}</div>;

  return (
    <div
      style={{ padding: 24, backgroundColor: "#f8fafc", minHeight: "100vh" }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: "#1e293b",
            fontSize: "2rem",
            mb: 1,
          }}
        >
          Service Reports
        </Typography>
      </Box>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Popular Services Chart - Full Width */}
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid #e2e8f0",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.5rem",
                }}
              >
                Popular Services
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={showAllServices ? "outlined" : "contained"}
                  onClick={() => setShowAllServices(!showAllServices)}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "8px",
                    px: 2,
                    py: 0.75,
                    fontSize: "0.875rem",
                    ...(showAllServices
                      ? {
                          borderColor: "#64748b",
                          color: "#64748b",
                          "&:hover": {
                            borderColor: "#475569",
                            backgroundColor: "#f8fafc",
                          },
                        }
                      : {
                          backgroundColor: "#3b82f6",
                          "&:hover": {
                            backgroundColor: "#2563eb",
                          },
                        }),
                  }}
                >
                  {showAllServices ? "Top 10" : "View All"}
                </Button>
              </Box>
            </Box>
            <Box sx={{ height: "400px" }}>
              <Bar
                data={popularServicesData}
                options={popularServicesOptions}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Clients by Type Chart */}
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid #e2e8f0",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.5rem",
                }}
              >
                Client Distribution by Type
              </Typography>
              <Button
                variant={showAllServicesByType ? "outlined" : "contained"}
                onClick={() => setShowAllServicesByType(!showAllServicesByType)}
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "8px",
                  px: 2,
                  py: 0.75,
                  fontSize: "0.875rem",
                  ...(showAllServicesByType
                    ? {
                        borderColor: "#64748b",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#475569",
                          backgroundColor: "#f8fafc",
                        },
                      }
                    : {
                        backgroundColor: "#3b82f6",
                        "&:hover": {
                          backgroundColor: "#2563eb",
                        },
                      }),
                }}
              >
                {showAllServicesByType ? "Top 8" : "View All"}
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                height: "400px",
              }}
            >
              <Pie
                data={servicesByTypeChartData}
                options={servicesByTypeOptions}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Data Table */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <TextField
            label="Search Services"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: "1 1 200px", minWidth: "200px" }}
          />
          <FormControl sx={{ flex: "1 1 200px", minWidth: "200px" }}>
            <InputLabel>Filter by Client Type</InputLabel>
            <Select
              value={clientTypeFilter}
              onChange={(e) => setClientTypeFilter(e.target.value)}
              label="Filter by Client Type"
            >
              <MenuItem value="">All Client Types</MenuItem>
              {uniqueClientTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Data Table */}
        <DataTable
          title="Service Analysis"
          columns={columns}
          data={filteredServices}
          pagination
          highlightOnHover
          striped
          customStyles={customStyles}
          responsive
          paginationPerPage={25}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
        />

        {/* Export Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={downloadCSV}
            sx={{
              backgroundColor: "#059669",
              color: "white",
              borderRadius: "8px",
              padding: "10px 24px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              boxShadow:
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              "&:hover": {
                backgroundColor: "#047857",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              },
            }}
          >
            Export Data
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default ServiceReports;
